import React from 'react';
import Flexbox from 'flexbox-react';
import { Card } from '@blueprintjs/core';
import ContainerIntroBox from '../../components/goal_performance/ContainerIntroBox.js';
import AssessmentReadOnlyList from '../../components/goal_performance/AssessmentReadOnlyList.js';
import GoalReadOnlyList from '../../components/goal_performance/GoalReadOnlyList.js';
import EmptyStateNotice from '../../components/goal_performance/EmptyStateNotice.js';
import ReadOnlyNotice from '../../components/goal_performance/ReadOnlyNotice.js';

const ReviewTabContent = ({
  step,
  reviewSubmittedListByManager,
  reviewSubmittedCatGroupListByManager,
  goalSubmittedListByManager,
  reviewRatingByManager,
  reviewRatingLabelByManager,
  goalRatingByManager,
  goalRatingLabelByManager,
  responseFinalRating,
  responseFinalRatingLabel,
  selectedTimeframe,
  resultData,
  managerGlobalRating,
  managerGlobalRatingLabel,
  accountId,
  accountVisibilityConfig,
  subGoalsByGoalId,
  openSubGoals,
  toggleSubGoals,
  reviewStepStatus,
  multiLevelReview,
}) => {
  if (!step) {
    return (
      <Card className="assessment-answer-container">
        <EmptyStateNotice
          title="Waiting for Manager Review"
          description="Your manager is still working on this review step. You'll be notified once it's completed."
        />
      </Card>
    );
  }

  const status = Number(reviewStepStatus ?? step?.is_review_submitted ?? 0) || 0;

  const hasAssessmentData = (reviewSubmittedListByManager?.length || 0) > 0;
  const hasGoalData = (goalSubmittedListByManager?.length || 0) > 0;
  const hasAnyData = hasAssessmentData || hasGoalData;

  const shouldRenderData = status === 2;

  if (shouldRenderData) {
    return (
      <>
        <Flexbox className="self-assessment-container" flexDirection="column">
          {hasAssessmentData && (
            <>
              <ContainerIntroBox
                iconType="manager"
                text="Manager's Review of Your Performance"
                description={
                  'View feedback from your manager on your performance, strengths, and growth areas'
                }
                rating={reviewRatingByManager}
                ratingLabel={reviewRatingLabelByManager}
                ratingLabels={selectedTimeframe?.rating_labels || []}
                finalAssessmentRatingLabel={responseFinalRatingLabel}
                userGlobalRating={responseFinalRating}
                userGlobalRatingLabel={responseFinalRatingLabel}
                managerGlobalRating={managerGlobalRating}
                managerGlobalRatingLabel={managerGlobalRatingLabel}
                isRatingEnabledQuestions={
                  selectedTimeframe?.is_assessment_question_rating
                }
                containerType="assessment"
              />
              <ReadOnlyNotice />

              <Card className="assessment-answer-container">
                <AssessmentReadOnlyList
                  data={
                    reviewSubmittedCatGroupListByManager?.length > 0
                      ? reviewSubmittedCatGroupListByManager
                      : reviewSubmittedListByManager
                  }
                  finalAssessmentRating={reviewRatingByManager}
                  weightage={selectedTimeframe?.is_weight_allow}
                  ratingLabels={selectedTimeframe?.rating_labels || []}
                  isRatingEnabledQuestions={
                    selectedTimeframe?.is_assessment_question_rating
                  }
                  accountId={accountId}
                  visibilityConfig={accountVisibilityConfig}
                  isAnswerAllowedOnQuestion={
                    selectedTimeframe?.is_answer_allow_on_question
                  }
                />
              </Card>
            </>
          )}

          {hasGoalData && (
            <>
              <ContainerIntroBox
                iconType="manager"
                text="Manager's Review of Your Goal Performance"
                description={
                  'View feedback from your manager on your performance, strengths, and growth areas'
                }
                rating={goalRatingByManager}
                ratingLabel={goalRatingLabelByManager}
                ratingLabels={selectedTimeframe?.rating_labels || []}
                finalGoalRatingLabel={goalRatingLabelByManager}
                containerType="goal"
                isRatingEnabledQuestions={
                  selectedTimeframe?.is_assessment_question_rating
                }
              />
              <Card className="assessment-answer-container">
                <GoalReadOnlyList
                  data={goalSubmittedListByManager}
                  goalFinalRating={goalRatingByManager}
                  weightage={selectedTimeframe?.is_weight_allow}
                  ratingLabels={selectedTimeframe?.rating_labels || []}
                  isMultiLevelReview={multiLevelReview}
                  managerView={false}
                  subGoalsByGoalId={subGoalsByGoalId}
                  openSubGoals={openSubGoals}
                  toggleSubGoals={toggleSubGoals}
                />
              </Card>
            </>
          )}

          {!hasAnyData && (
            <Card className="assessment-answer-container">
              <EmptyStateNotice
                title="Waiting for Manager Review"
                description="Your manager is still working on this review step. You'll be notified once it's completed."
              />
            </Card>
          )}
        </Flexbox>
      </>
    );
  } else if (status === 0 || status === 1) {
    return (
      <Card className="assessment-answer-container">
        <EmptyStateNotice
          title="Waiting for Manager Review"
          description="Your manager is still working on this review step. You'll be notified once it's completed."
        />
      </Card>
    );
  } else if (status === 3) {
    const managerName =
      step?.skipped_by_name || step?.assigned_user_name || 'the manager';

    return (
      <Card className="assessment-answer-container">
        <EmptyStateNotice
          title="Review Skipped"
          description={
            <>
              This review step was skipped by <strong>{managerName}</strong>.
            </>
          }
        />
      </Card>
    );
  }

  return (
    <Card className="assessment-answer-container">
      <EmptyStateNotice
        title="Unknown Status"
        description="The status of this review step is unknown."
      />
    </Card>
  );
};

export default ReviewTabContent;
